///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewContainerRef
} from '@angular/core';
import { GtthValidationMessageComponent } from './validation-message.component';
import { FormControl, NG_VALIDATORS, NgForm, Validator } from '@angular/forms';

@Directive({
    selector: '[windeNumberInputValidation]',
    providers: [{ provide: NG_VALIDATORS, useExisting: GtthNumberInputValidationDirective, multi: true }]
})
export class GtthNumberInputValidationDirective implements OnChanges, OnInit, AfterViewInit, Validator, OnDestroy {
    @Input() inputVal: any;
    @Input() decimal: number;
    @Input() showValidationText: boolean;
    @Input() editForm: NgForm;
    @Input() inputIsRequired: boolean;

    @Output() inputValChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() inputValidated = new EventEmitter();

    private min: number;
    private max: number;
    private isRequired: boolean;
    private inputName: string;
    private validationMessageComponentRef;
    private message: string;
    private showMessage: boolean;
    private validationTimer;
    private validationTimeOut = 0;
    private outOfRange: boolean;

    private validationRule = /[^\d-]/gi;
    private decimalValidationRule = /[^\d-.]/gi;

    private pasteText: string;

    constructor(
        private readonly el: ElementRef,
        private readonly viewContainerRef: ViewContainerRef,
        private readonly ngZone: NgZone
    ) {}

    ngOnChanges() {
        this.runValidation();
    }

    ngOnInit() {
        this.el.nativeElement.onpaste = (e) => {
            this.pasteText = e.clipboardData.getData('Text');
            this.runValidation();
        };
        this.addValidationMessageComponent();
    }

    ngOnDestroy() {
        this.min = null;
        this.max = null;
        this.isRequired = null;
        this.inputName = null;
        this.validationMessageComponentRef = null;
        this.message = null;
        this.showMessage = null;
        this.validationTimer = null;
        this.validationTimeOut = null;
        this.outOfRange = null;
        this.validationRule = null;
        this.decimalValidationRule = null;
        this.pasteText = null;
    }

    ngAfterViewInit(): void {
        this.getInputParams();
    }

    validate(formControl: FormControl): Record<string, any> {
        this.getInputParams();
        const inpValue = formControl.value;
        if (!inpValue && inpValue !== 0 && this.isRequired) {
            return { invalid: true };
        }
        this.outOfRange = (this.min && inpValue < this.min) || (this.max && inpValue > this.max);
        this.setValidationMessage();
        return this.outOfRange ? { outOfRange: true } : null;
    }

    @HostListener('keydown', ['$event'])
    OnKeydown(event): boolean | undefined {
        return this.processKeydownEvent(event);
    }

    private runValidation() {
        this.getInputParams();
        this.ngZone.runOutsideAngular(() => {
            this.validationTimer = setTimeout(() => {
                this.ngZone.run(() => {
                    this.customValidate();
                    this.inputValChange.emit(this.inputVal);
                    this.inputValidated.emit();
                });
            }, this.validationTimeOut);
        });
    }

    private customValidate() {
        this.getInputParams();

        if (!this.inputVal && this.inputVal !== 0 && this.pasteText) {
            this.inputVal = this.pasteText;
        }

        this.pasteText = undefined;

        if (this.inputVal || this.inputVal === 0) {
            let valueString = this.inputVal.toString();
            if ((this.min || this.min === 0) && this.min >= 0) {
                valueString = valueString.replace(/-/gi, '');
            }
            if (!this.decimal) {
                valueString = valueString.replace(this.validationRule, '');
                this.inputVal = parseInt(valueString, 10);
            } else {
                valueString = parseFloat(valueString.replace(this.decimalValidationRule, ''));
                this.inputVal = parseFloat(valueString.toFixed(this.decimal));
            }

            if (this.max && this.inputVal > this.max) {
                this.inputVal = this.max;
            }
        }

        this.setValidationMessage();
    }

    private addValidationMessageComponent() {
        this.viewContainerRef.clear();
        this.validationMessageComponentRef = this.viewContainerRef.createComponent(GtthValidationMessageComponent);
    }

    private setValidationMessage() {
        if (!this.validationMessageComponentRef) {
            return;
        }
        if (this.showValidationText) {
            this.showMessage = this.showValidationText;
        }
        if (this.showMessage) {
            if (!this.inputVal && this.inputVal !== 0 && (this.isRequired || this.inputIsRequired)) {
                this.message = 'Field is required';
            } else if (this.outOfRange) {
                this.message = 'Value out of range';
            } else {
                this.message = '';
            }
            (this.validationMessageComponentRef.instance as GtthValidationMessageComponent).message = this.message;
        }
    }

    private getInputParams() {
        this.min = parseFloat(this.el.nativeElement.getAttribute('min'));
        this.max = parseFloat(this.el.nativeElement.getAttribute('max'));
        this.isRequired = this.el.nativeElement.required;
        this.inputName = this.el.nativeElement.getAttribute('name');
    }

    private processKeydownEvent(event) {
        if (event.key === 'e' || event.key === '+') {
            return false;
        }

        if ((this.min || this.min === 0) && this.min >= 0 && event.key === '-') {
            return false;
        }

        if (!this.decimal && event.key === '.') {
            return false;
        }
    }
}
